import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/components/ui/collapsible';
import { SCHEDULES_PAGE_SIZE } from '~/features/catalog-operations/constants/schedules';
import {
  useScheduleDetail,
  useSchedules,
  useSchedulesRibbon,
} from '~/features/catalog-operations/hooks/use-catalog-ops-schedules';
import type {
  ScheduleListFilters,
  SchedulesRibbon as SchedulesRibbonData,
  ScheduleSummary,
} from '~/features/catalog-operations/types/catalog-ops-schedule.types';
import { cn } from '~/utils/cn';
import { formatInZone, getLocalTimeZone } from '~/utils/date';

import { SchedulesFilterBar } from './schedules-filter-bar';
import { SchedulesRibbon } from './schedules-ribbon';
import { SchedulesTable } from './schedules-table';

/**
 * Collapsed summary line. A closed section must not hide a problem, so the
 * counts, the soonest upcoming run and a failing badge all live in the header —
 * the table underneath adds detail, not information you need to know.
 */
function SectionSummary({
  schedules,
  ribbon,
}: {
  schedules: ScheduleSummary[];
  ribbon: SchedulesRibbonData | undefined;
}) {
  const { t } = useTranslation('catalog-operations');

  if (!ribbon || ribbon.total === 0) {
    return (
      <span data-testid="schedules-section-summary-empty" className="text-muted-foreground text-sm">
        {t('schedules.section.none')}
      </span>
    );
  }

  // Counts come from the ribbon: those are computed over the whole set, so they
  // stay right no matter how many pages there are.
  const { active, paused } = ribbon;
  // The next run and the failing badge are derived from the loaded page — the
  // list DTO is the only place either value exists. With one page (20 rows) that
  // is the whole set; beyond that the badge under-reports rather than lying
  // about a schedule that is fine.
  const failing = schedules.filter((s) => s.health === 'FAILING').length;
  const soonest = schedules
    .filter((s) => s.status === 'ACTIVE' && s.nextRunAt)
    .sort((a, b) => (a.nextRunAt ?? '').localeCompare(b.nextRunAt ?? ''))
    .at(0);

  return (
    <span
      data-testid="schedules-section-summary"
      className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
    >
      <span data-testid="schedules-section-summary-active">
        {t('schedules.section.activeCount', { count: active })}
      </span>
      {paused > 0 && (
        <>
          <span aria-hidden="true">·</span>
          <span data-testid="schedules-section-summary-paused">
            {t('schedules.section.pausedCount', { count: paused })}
          </span>
        </>
      )}
      <span aria-hidden="true">·</span>
      <span data-testid="schedules-section-summary-next-run">
        {soonest?.nextRunAt
          ? t('schedules.section.nextRun', {
              when: formatInZone(soonest.nextRunAt, getLocalTimeZone()),
            })
          : t('schedules.section.noUpcoming')}
      </span>
      {failing > 0 && (
        <span
          data-testid="schedules-section-failing-badge"
          className="bg-error-50 text-error rounded-full px-2 py-0.5 text-xs font-medium"
        >
          {t('schedules.section.failingCount', { count: failing })}
        </span>
      )}
    </span>
  );
}

/**
 * The Schedules section on the Catalog Operations page — collapsed by default so
 * it costs one row next to the batch table, and expands to the ribbon, filters
 * and list.
 *
 * The list and ribbon queries share one filter object, so a filter change always
 * refetches both and the metrics can never describe a different filter than the
 * rows beneath them.
 */
export function SchedulesSection({
  focus,
}: {
  /** Deep link (`?tab=schedules[&scheduleId=]`): opens the section and, given an id, narrows
   *  the list to that schedule's name and highlights its row. */
  focus?: { scheduleId?: string | undefined } | undefined;
}) {
  const { t } = useTranslation('catalog-operations');
  const [open, setOpen] = useState(focus !== undefined);
  const [filters, setFilters] = useState<ScheduleListFilters>({ size: SCHEDULES_PAGE_SIZE });
  const sectionRef = useRef<HTMLDivElement>(null);

  // The list is cursor-paged, so the focused schedule may not be on page one: once its detail
  // resolves, search by its name (render-time, applied once per id) so the row is guaranteed
  // to load. A 404 (deleted / another tenant) just leaves the section open and unfiltered.
  const focusId = focus?.scheduleId ?? null;
  const { data: focused } = useScheduleDetail(focusId);
  const [appliedFocusId, setAppliedFocusId] = useState<string | null>(null);
  if (focused && focusId && focused.id === focusId && appliedFocusId !== focusId) {
    setAppliedFocusId(focusId);
    setFilters((prev) => ({ ...prev, search: focused.name, cursor: undefined }));
  }

  // Bring the section into view on arrival — it sits below the stat cards. Keyed on primitives:
  // the `focus` object is rebuilt on every render of the route.
  const hasFocus = focus !== undefined;
  useEffect(() => {
    if (hasFocus) sectionRef.current?.scrollIntoView({ block: 'start' });
  }, [hasFocus, focusId]);

  // Not gated on `open`: the collapsed header shows live counts and the next
  // run, so both queries have to resolve before anyone expands the section.
  const { data, isLoading } = useSchedules(filters);
  const { data: ribbon, isLoading: isRibbonLoading } = useSchedulesRibbon(filters);

  const schedules = data?.items ?? [];
  const isFiltered =
    filters.status !== undefined ||
    filters.connector !== undefined ||
    filters.health !== undefined ||
    (filters.search ?? '') !== '';

  return (
    <>
      <Collapsible
        ref={sectionRef}
        data-testid="schedules-section"
        open={open}
        onOpenChange={setOpen}
        className="bg-background rounded-xl border"
      >
        <CollapsibleTrigger
          data-testid="schedules-section-toggle"
          className="hover:bg-muted flex w-full cursor-pointer items-center gap-4 rounded-xl px-4 py-3.5 text-left transition-colors"
        >
          <span className="flex shrink-0 items-center gap-2">
            <span data-testid="schedules-section-title" className="text-base font-semibold">
              {t('schedules.section.title')}
            </span>
            {ribbon && (
              <span
                data-testid="schedules-section-total-badge"
                className="bg-accent-100 text-primary rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums"
              >
                {ribbon.total}
              </span>
            )}
          </span>
          <span className="min-w-0 flex-1">
            {/* Hidden while expanded (the ribbon says it better) and while the
                first fetch is in flight — a summary that reads "0 active"
                before the data lands is worse than no summary. */}
            {open || isLoading ? null : <SectionSummary schedules={schedules} ribbon={ribbon} />}
          </span>
          <ChevronDown
            className={cn(
              'text-muted-foreground size-4 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </CollapsibleTrigger>

        <CollapsibleContent data-testid="schedules-section-content">
          <div className="space-y-3 border-t px-4 pt-4 pb-4">
            <SchedulesRibbon data={ribbon} isLoading={isRibbonLoading} />
            <SchedulesFilterBar
              testId="schedules-filter"
              filters={filters}
              onChange={setFilters}
              shown={schedules.length}
              total={ribbon?.total}
            />
            <SchedulesTable
              schedules={schedules}
              isLoading={isLoading}
              isFiltered={isFiltered}
              highlightId={focusId}
            />
            {data?.nextCursor && (
              <p
                data-testid="schedules-more-pages"
                className="text-muted-foreground text-center text-xs"
              >
                {t('schedules.list.morePages')}
              </p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
